'use client';

import { RtText } from '@dpa/react-text';
import { useState } from 'react';
import classnames from 'classnames/bind';
import { useTranslations } from 'next-intl';
import { articleRateEvent } from '@/_analytic/events/article-rate-event';
import { articleFeedbackCloseEvent } from '@/_analytic/events/article-feedback-close-event';
import { articleFeedbackEvent } from '@/_analytic/events/article-feedback-event';
import { IMaterial } from '@/_types';
import { useMaterialAnalyticData } from '@/_analytic/hooks/use-material-analytic-data';
import { TFeedbackData } from './_components/feedback-modal/_types';
import { FeedbackModal } from './_components/feedback-modal';
import {
  INITIAL_RATING,
  MIN_RATING_TO_SKIP_FEEDBACK_DETAILS,
  STARS,
} from './_constants';
import { FeedbackSuccessModal } from './_components/feedback-success-modal';
import { getFeedbackReplies } from './_utils/get-feedback-replies';
import { StarChips } from './_components/star-chips';
import styles from './index.module.scss';

const BLOCK_NAME = 'Feedback';
const cn = classnames.bind(styles);

export type TFeedbackProps = {
  materialId: IMaterial['id'];
  materialName: IMaterial['title'];
  materialCategoryId?: string;
  isInDrawer?: boolean;
};
export const Feedback = ({
  materialId,
  materialName,
  materialCategoryId,
  isInDrawer,
}: TFeedbackProps) => {
  const translations = useTranslations('article');

  const [rating, setRating] = useState<number>(INITIAL_RATING);
  const [open, setOpen] = useState<boolean>(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setRating(INITIAL_RATING);
  };

  const [isRated, setIsRated] = useState(false);

  const [successOpen, setSuccessOpen] = useState<boolean>(false);
  const handleSuccessModalOpen = () => setSuccessOpen(true);
  const handleSuccessModalClose = () => setSuccessOpen(false);

  const commonAnalyticData = useMaterialAnalyticData({
    materialTitle: materialName,
    materialId,
    materialCategoryId,
  });

  const handleArticleRate = (rate: number) => {
    articleRateEvent({
      rate,
      ...commonAnalyticData,
    });
  };

  const handleArticleRateWithDetailsClose = (rate: number) => {
    articleFeedbackCloseEvent({
      rate,
      ...commonAnalyticData,
    });
  };

  const handleRate = ({
    ratingValue,
  }: {
    ratingValue: number;
    complaints?: Array<string>;
    comment?: string;
  }) => {
    setIsRated(true);
    setRating(ratingValue);
  };
  const handleUpdateRating = (value: number) => {
    setRating(value);
    handleArticleRate(value);
    handleOpen();
  };

  const handleSubmit = (data: TFeedbackData) => {
    const { comment, ...rest } = data;
    const negativeRate = rating < MIN_RATING_TO_SKIP_FEEDBACK_DETAILS;
    const feedbackReplies = negativeRate
      ? getFeedbackReplies(rest)
      : 'no choose: rate 4 or 5';

    articleFeedbackEvent({
      ...commonAnalyticData,
      rate: rating,
      textComment: comment || '',
      chosenSuggests: feedbackReplies,
    });
    handleRate({ ratingValue: rating });
    handleSuccessModalOpen();
  };

  const handleCloseEvent = () => {
    handleArticleRateWithDetailsClose(rating);
  };

  const text = translations('feedbackTitle');
  const stars = Array.from({ length: STARS });

  return (
    <>
      {!isRated && (
        <div className={cn(BLOCK_NAME)}>
          <RtText
            specification="main"
            typography="body-xs-medium"
            className={cn(`${BLOCK_NAME}__text`)}
          >
            {text}
          </RtText>
          <div className={cn(`${BLOCK_NAME}__rating`)}>
            {stars.map((_, index) => (
              <StarChips
                key={index}
                index={index}
                handleUpdateRating={handleUpdateRating}
              />
            ))}
          </div>
        </div>
      )}
      <FeedbackModal
        isInDrawer={isInDrawer}
        isOpened={open}
        onClose={handleClose}
        onCloseEvent={handleCloseEvent}
        onSubmit={handleSubmit}
        rating={rating}
      />
      <FeedbackSuccessModal
        isInDrawer={isInDrawer}
        isLoading={false}
        isOpened={successOpen}
        onClose={handleSuccessModalClose}
      />
    </>
  );
};
